import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Grid, Spinner, Text, VStack, toaster, useT } from '@minetrace/ui'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuArrowLeft,
  LuArrowUpRight,
  LuChevronDown,
  LuCircleCheck,
  LuCircleHelp,
  LuDownload,
  LuFolderOpen,
  LuFolderTree,
  LuLink2,
  LuStamp,
  LuTriangleAlert,
} from 'react-icons/lu'
import { expedienteService } from '../../../api/services'
import type {
  ExpedienteIndiceResponse,
  IndiceEntradaResponse,
  IndiceVerificacionResponse,
  IndiceVersionResponse,
} from '../../../api/models/response/indiceDigital'
import { useGate } from '../../../auth/PermissionGate'
import { accentFor, brand, fonts, mineral, shadows, themeColors } from '../../../theme'
import { formatDate, formatDateTime } from '../../../lib/dates'
import AnimatedNumber from '../../../components/brand/AnimatedNumber'
import { Chip } from './ui'
import { downloadAuthenticated } from './helpers'
import { IndiceTable } from './IndiceTable'
import { MetadatosTab } from './MetadatosTab'
import { VersionesTab } from './VersionesTab'
import { RelacionadosTab } from './RelacionadosTab'
import { VerificacionPanel } from './VerificacionPanel'
import { EntradaDrawer } from './EntradaDrawer'
import { describeVerificacion } from './verificacion'
import { resolveUbigeoLabel } from '../../../lib/ubigeo'

const MotionBox = motion.create(Box)

/** Expedientes vive en Identidad: rodocrosita. */
const acc = accentFor('identity')

type TabKey = 'indice' | 'metadatos' | 'versiones' | 'relacionados'

const TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'indice', labelKey: 'expedientes.electronico.tabs.indice' },
  { key: 'metadatos', labelKey: 'expedientes.electronico.tabs.metadatos' },
  { key: 'versiones', labelKey: 'expedientes.electronico.tabs.versiones' },
  { key: 'relacionados', labelKey: 'expedientes.electronico.tabs.relacionados' },
]

/**
 * VISTA ARCHIVÍSTICA del expediente (D.S. 029-2021-PCM). Es la hermana de
 * `DetalleExpediente`, que sigue siendo la OPERATIVA: allí se trabaja el dosier
 * (árbol, secciones plegables, descarga por ítem); aquí se lee el índice digital
 * como lo leería un auditor — foliado corrido, series documentales, versiones
 * selladas y sus veredictos.
 *
 * NO SE MEZCLAN. La operativa agrupa por jerarquía; ésta va por folio.
 *
 * La cabecera es la carpeta: a la izquierda quién es y cuánto pesa (folios,
 * documentos, series, versiones); a la derecha, el SELLO de la última versión con
 * su CVD. El veredicto completo se despliega a pedido para no tapar el índice.
 */
export default function ExpedienteElectronicoPage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { t } = useT()
  const canEmit = useGate({ permission: 'expedientes.indice.emit' })

  const [indice, setIndice] = useState<ExpedienteIndiceResponse | null>(null)
  const [versiones, setVersiones] = useState<IndiceVersionResponse[]>([])
  const [verificaciones, setVerificaciones] = useState<Record<number, IndiceVerificacionResponse>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabKey>('indice')
  const [sealing, setSealing] = useState(false)
  const [downloadingZip, setDownloadingZip] = useState(false)
  const [selected, setSelected] = useState<IndiceEntradaResponse | null>(null)
  const [showVerify, setShowVerify] = useState(false)

  const latest = versiones[0] ?? null
  const latestVerificacion = latest ? (verificaciones[latest.versionNumber] ?? null) : null

  const load = useCallback(async (signal?: { cancelled: boolean }) => {
    if (!code) return
    setLoading(true)
    setError(null)
    try {
      // El índice y las versiones son independientes: se piden a la vez.
      const [indiceData, versionesData] = await Promise.all([
        expedienteService.getIndice(code),
        expedienteService.getIndiceVersiones(code),
      ])
      if (signal?.cancelled) return
      setIndice(indiceData)
      setVersiones(versionesData)

      // El veredicto de la ÚLTIMA versión se pide solo: es lo que se enseña en la
      // cabecera. Las demás se comprueban a petición desde la pestaña de versiones.
      const head = versionesData[0]
      if (head) {
        try {
          const v = await expedienteService.verifyIndiceVersion(code, head.versionNumber)
          if (!signal?.cancelled) setVerificaciones((prev) => ({ ...prev, [v.versionNumber]: v }))
        } catch {
          // Sin veredicto la cabecera se limita a no enseñar chip. No es un error de
          // la pantalla y no debe tumbar la carga del índice.
        }
      }
    } catch (e) {
      if (!signal?.cancelled) {
        setError(e instanceof Error ? e.message : t('expedientes.electronico.notFound'))
      }
    } finally {
      if (!signal?.cancelled) setLoading(false)
    }
  }, [code, t])

  useEffect(() => {
    const signal = { cancelled: false }
    load(signal)
    return () => { signal.cancelled = true }
  }, [load])

  const handleSeal = async () => {
    if (!code) return
    setSealing(true)
    try {
      const v = await expedienteService.sealIndice(code)
      toaster.success({
        description: t('expedientes.electronico.seal.success', { version: v.versionNumber, cvd: v.cvd }),
      })
      await load()
      setTab('versiones')
    } catch (e) {
      toaster.error({
        title: t('expedientes.electronico.seal.error'),
        description: e instanceof Error ? e.message : undefined,
      })
    } finally {
      setSealing(false)
    }
  }

  const handleDownloadZip = async () => {
    if (!code) return
    setDownloadingZip(true)
    try {
      await downloadAuthenticated(expedienteService.getDownloadZipUrl(code), `${code}.zip`)
      toaster.success({ description: t('expedientes.electronico.downloadZipSuccess') })
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al descargar el ZIP' })
    } finally {
      setDownloadingZip(false)
    }
  }

  if (loading) {
    return (
      <VStack gap={4} py={20}>
        <Spinner size="lg" color={acc.color} />
        <Text fontSize="sm" color={themeColors.textMuted}>{t('expedientes.electronico.loading')}</Text>
      </VStack>
    )
  }

  if (error || !indice || !code) {
    return (
      <VStack gap={4} py={20}>
        <Text fontSize="lg" color={themeColors.text}>{error ?? t('expedientes.electronico.notFound')}</Text>
        <Box
          as="button"
          color={acc.deep}
          fontSize="sm"
          cursor="pointer"
          onClick={() => navigate('/app/identidad/expedientes')}
        >
          {t('expedientes.detail.backToSearch')}
        </Box>
      </VStack>
    )
  }

  const meta = indice.metadatos
  const isBatch = meta.tipo === 1
  const closed = !!meta.fechaCierre
  const verdict = latestVerificacion ? describeVerificacion(latestVerificacion) : null
  const counts = [
    { value: meta.totalFolios, label: t('expedientes.electronico.counts.folios') },
    { value: meta.totalDocumentos, label: t('expedientes.electronico.counts.documents') },
    { value: indice.resumenPorSerie.length, label: t('expedientes.electronico.counts.series') },
    { value: versiones.length, label: versiones.length === 1 ? 'versión sellada' : 'versiones selladas' },
  ]
  const tabCount: Partial<Record<TabKey, number>> = {
    indice: indice.entradas.length,
    versiones: versiones.length,
    relacionados: indice.relacionados.length + (indice.expedienteOrigen ? 1 : 0),
  }
  const sealColor = verdict?.tone === 'alarm' ? mineral.aji : mineral.sabanaDeep

  return (
    <VStack gap={5} alignItems="stretch">
      {/* Vuelta al listado + puente a la vista operativa */}
      <Flex alignItems="center" gap={3} flexWrap="wrap">
        <Flex as="button" {...{ type: 'button' }} align="center" gap={2} color={mineral.ink3} fontSize="13.5px" fontWeight={500}
          cursor="pointer" _hover={{ color: acc.deep }} transition="color .15s"
          onClick={() => navigate('/app/identidad/expedientes')}>
          <LuArrowLeft size={16} /> {t('expedientes.title')}
        </Flex>
        <Box flex="1" />
        <Flex as="button" {...{ type: 'button' }} alignItems="center" gap={1.5} px={3} h="32px" borderRadius="full"
          border={`1px solid ${mineral.line}`} bg={mineral.surface} color={mineral.ink2} fontSize="12.5px" fontWeight={600}
          cursor="pointer" _hover={{ borderColor: acc.color, color: acc.deep }} transition="all .15s"
          onClick={() => navigate(`/app/identidad/expedientes/${encodeURIComponent(code)}`)}>
          <LuFolderOpen size={14} />
          {t('expedientes.electronico.backToOperational')}
          <LuArrowUpRight size={13} />
        </Flex>
      </Flex>

      {/* Cabecera archivística: la carpeta y su sello */}
      <MotionBox
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        position="relative"
        bg={mineral.surface}
        border={`1px solid ${mineral.line}`}
        borderRadius="22px"
        boxShadow={shadows.md}
        overflow="hidden"
      >
        {/* Lomo de la carpeta, en el color del módulo */}
        <Box position="absolute" left={0} top={0} bottom={0} w="5px" bg={acc.color} />

        <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 400px' }}>
          {/* Identidad del expediente */}
          <Box px={{ base: 6, md: 8 }} py={6} minW={0}>
            <Flex align="center" gap={2.5} wrap="wrap">
              <Flex align="center" gap={1.5} color={acc.deep}>
                <LuFolderTree size={14} />
                <Text fontFamily={fonts.mono} fontSize="10.5px" letterSpacing="0.14em" textTransform="uppercase">
                  {t('expedientes.electronico.title')} · {meta.tipoNombre}
                </Text>
              </Flex>
              <Box px={2} py="2px" borderRadius="6px" fontSize="10.5px" fontWeight={700} textTransform="uppercase" letterSpacing="0.05em"
                bg={!isBatch || closed ? mineral.sabanaSoft : mineral.oroSoft} color={!isBatch || closed ? mineral.sabanaDeep : mineral.oroDeep}>
                {isBatch ? (closed ? 'Cerrado' : 'Abierto') : meta.estadoNombre}
              </Box>
            </Flex>

            <Text fontFamily={fonts.mono} fontWeight={700} fontSize={{ base: '24px', md: '30px' }} color={mineral.ink} letterSpacing="-0.01em" mt={2} lineHeight="1.1">
              {meta.codigoUnico}
            </Text>
            <Text fontFamily={fonts.display} fontWeight={700} fontSize="18px" color={mineral.ink2} letterSpacing="-0.01em" mt={1}>
              {meta.titularRazonSocial ?? '—'}
            </Text>
            <Flex gap={4} mt={2} wrap="wrap" fontSize="12.5px" color={mineral.ink2}>
              {meta.titularRuc && (
                <Text><Text as="span" color={mineral.ink4}>RUC </Text><Text as="span" fontFamily={fonts.mono}>{meta.titularRuc}</Text></Text>
              )}
              {meta.cuidCode && (
                <Text><Text as="span" color={mineral.ink4}>{t('expedientes.electronico.cuid')} </Text><Text as="span" fontFamily={fonts.mono}>{meta.cuidCode}</Text></Text>
              )}
              {/* Un lote no pasa por aprobación: su fecha es la de creación del expediente. */}
              {isBatch ? (
                <Text><Text as="span" color={mineral.ink4}>{t('expedientes.electronico.created')} </Text>{formatDate(indice.fechaCreacion ?? null)}</Text>
              ) : (
                <Text><Text as="span" color={mineral.ink4}>{t('expedientes.electronico.opened')} </Text>{formatDate(meta.fechaApertura)}</Text>
              )}
              <Text><Text as="span" color={mineral.ink4}>{t('expedientes.electronico.lastUpdate')} </Text>{formatDateTime(meta.fechaUltimaActualizacion)}</Text>
            </Flex>
            {isBatch && (indice.derechoMinero || indice.ubigeo) && (
              <Flex gap={4} mt={1.5} wrap="wrap" fontSize="12.5px" color={mineral.ink2}>
                {indice.derechoMinero && (
                  <Text><Text as="span" color={mineral.ink4}>{t('expedientes.electronico.miningRight')} </Text>{indice.derechoMinero}</Text>
                )}
                {indice.ubigeo && (
                  <Text>
                    <Text as="span" color={mineral.ink4}>{t('expedientes.electronico.ubigeo')} </Text>
                    {resolveUbigeoLabel(indice.ubigeo)}{' '}
                    <Text as="span" fontFamily={fonts.mono} color={mineral.ink4}>({indice.ubigeo})</Text>
                  </Text>
                )}
              </Flex>
            )}

            {/* Recuentos, como un libro de registro */}
            <Flex mt={5} pt={4} borderTop={`1px dashed ${mineral.lineStrong}`} wrap="wrap" rowGap={3}>
              {counts.map((c, i) => (
                <Box key={c.label} pr={6} mr={6} py={0.5} borderRight={i < counts.length - 1 ? `1px solid ${mineral.line}` : 'none'}>
                  <Text fontFamily={fonts.display} fontWeight={800} fontSize="26px" color={mineral.ink} lineHeight="1" letterSpacing="-0.03em">
                    <AnimatedNumber value={c.value} />
                  </Text>
                  <Text fontSize="12px" color={mineral.ink3} mt={1}>{c.label}</Text>
                </Box>
              ))}
            </Flex>

            <Flex gap={2} mt={5} wrap="wrap">
              <Flex as="button" {...{ type: 'button', disabled: !canEmit || sealing, title: canEmit ? undefined : t('expedientes.electronico.seal.noPermission') }}
                align="center" gap={2} px={4} h="40px" borderRadius="11px" bg={brand.primary} color="#fff" fontSize="13.5px" fontWeight={600}
                cursor={canEmit && !sealing ? 'pointer' : 'not-allowed'} opacity={canEmit ? 1 : 0.5}
                _hover={canEmit && !sealing ? { bg: mineral.ocreDark } : {}} transition="background .15s"
                onClick={handleSeal}>
                {/* El sello "golpea" mientras se firma: dice qué está pasando */}
                <motion.span animate={sealing ? { rotate: [0, -14, 0], y: [0, 2, 0] } : { rotate: 0, y: 0 }}
                  transition={sealing ? { duration: 0.6, repeat: Infinity } : { duration: 0.2 }} style={{ display: 'flex' }}>
                  <LuStamp size={16} />
                </motion.span>
                {sealing ? t('expedientes.electronico.seal.emitting') : t('expedientes.electronico.seal.emit')}
              </Flex>
              <Flex as="button" {...{ type: 'button', disabled: downloadingZip }} align="center" gap={2} px={4} h="40px" borderRadius="11px"
                border={`1px solid ${mineral.line}`} bg={mineral.surface} color={mineral.ink2} fontSize="13.5px" fontWeight={600}
                cursor={downloadingZip ? 'progress' : 'pointer'} _hover={downloadingZip ? {} : { borderColor: acc.color, color: acc.deep }} transition="all .15s"
                onClick={handleDownloadZip}>
                <motion.span animate={downloadingZip ? { y: [0, 3, 0] } : { y: 0 }}
                  transition={downloadingZip ? { duration: 0.7, repeat: Infinity } : { duration: 0.2 }} style={{ display: 'flex' }}>
                  <LuDownload size={16} />
                </motion.span>
                {downloadingZip ? t('expedientes.electronico.downloadingZip') : t('expedientes.electronico.downloadZip')}
              </Flex>
            </Flex>
          </Box>

          {/* El sello del índice */}
          <Box px={{ base: 6, md: 7 }} py={6} bg={mineral.surfaceAlt}
            borderLeft={{ base: 'none', xl: `1px solid ${mineral.line}` }} borderTop={{ base: `1px solid ${mineral.line}`, xl: 'none' }}>
            {latest ? (
              <>
                <Flex align="center" gap={3}>
                  <motion.div initial={{ scale: 1.6, rotate: -20, opacity: 0 }} animate={{ scale: 1, rotate: -8, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 16, delay: 0.2 }}>
                    <Flex w="52px" h="52px" borderRadius="full" align="center" justify="center" color={sealColor} bg="#fff"
                      border="2px solid currentColor" boxShadow={`inset 0 0 0 3px #fff, inset 0 0 0 4px ${sealColor}`}>
                      <LuStamp size={22} />
                    </Flex>
                  </motion.div>
                  <Box minW={0}>
                    <Text fontFamily={fonts.display} fontWeight={700} fontSize="16px" color={mineral.ink}>
                      {t('expedientes.electronico.seal.signed', { version: latest.versionNumber })}
                    </Text>
                    <Text fontSize="12px" color={mineral.ink3}>
                      {t('expedientes.electronico.seal.sealedAt')} {formatDateTime(latest.generatedAt)}
                    </Text>
                  </Box>
                </Flex>

                <Box mt={4} px={4} py={3} borderRadius="12px" bg="#fff" border={`1px dashed ${mineral.lineStrong}`}>
                  <Text fontSize="10px" fontFamily={fonts.mono} letterSpacing="0.14em" color={mineral.ink3}>CÓDIGO DE VERIFICACIÓN DIGITAL</Text>
                  <Text fontFamily={fonts.mono} fontWeight={700} fontSize="20px" letterSpacing="0.08em" color={mineral.ink} mt={0.5}>{latest.cvd}</Text>
                </Box>

                <Flex gap={2} mt={3} wrap="wrap">
                  {/* El chip del veredicto se pinta con `describeVerificacion`, que
                      decide por severidad. `ModificadoDesdeElSello` sale en verde. */}
                  {verdict && (
                    <Chip
                      icon={verdict.tone === 'alarm' ? <LuTriangleAlert size={11} /> : verdict.tone === 'ok' ? <LuCircleCheck size={11} /> : <LuCircleHelp size={11} />}
                      color={verdict.color}
                      bg={verdict.bg}
                    >
                      {t(verdict.labelKey)}
                    </Chip>
                  )}
                  {meta.chainHead && (
                    <Chip icon={<LuLink2 size={11} />} color={mineral.sabanaDeep} bg={mineral.sabanaSoft}>
                      {t('expedientes.electronico.verify.chainIntact')}
                    </Chip>
                  )}
                  {latest.signingKeyIsEphemeral && (
                    <Chip icon={<LuTriangleAlert size={11} />} color={mineral.aji} bg={mineral.ajiSoft}>
                      {t('expedientes.electronico.versiones.ephemeral')}
                    </Chip>
                  )}
                </Flex>

                {latestVerificacion && (
                  <Flex as="button" {...{ type: 'button', 'aria-expanded': showVerify }} mt={3} align="center" gap={1} fontSize="12.5px" fontWeight={600}
                    color={acc.deep} cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => setShowVerify(v => !v)}>
                    {showVerify ? 'Ocultar la comprobación' : 'Ver la comprobación del sello'}
                    <Box display="flex" transition="transform .2s" transform={showVerify ? 'rotate(180deg)' : 'none'}><LuChevronDown size={14} /></Box>
                  </Flex>
                )}
              </>
            ) : (
              <>
                <Flex align="center" gap={3}>
                  <Flex w="52px" h="52px" borderRadius="full" align="center" justify="center" color={mineral.ink4} bg="#fff" border={`2px dashed ${mineral.lineStrong}`}>
                    <LuStamp size={22} />
                  </Flex>
                  <Text fontFamily={fonts.display} fontWeight={700} fontSize="16px" color={mineral.ink2}>{t('expedientes.electronico.seal.none')}</Text>
                </Flex>
                <Text fontSize="12.5px" color={mineral.ink3} mt={3} lineHeight="1.6">{t('expedientes.electronico.seal.noneHint')}</Text>
              </>
            )}

            {/* LA HONESTIDAD VA JUNTO AL ESTADO DE LA FIRMA, no enterrada. */}
            <Text fontSize="11px" color={mineral.ink3} mt={4} lineHeight="1.6">
              {t('expedientes.electronico.limits.iofeTitle')}
              {' · '}
              {t('expedientes.electronico.limits.tsaTitle')}
              {'. '}
              <Box as="button" {...{ type: 'button' }} color={acc.deep} fontWeight={600} cursor="pointer" textDecoration="underline" textUnderlineOffset="2px"
                onClick={() => setTab('metadatos')}>
                {t('expedientes.electronico.metadatos.limitsTitle')}
              </Box>
            </Text>
          </Box>
        </Grid>

        {/* Veredicto completo, desplegado a pedido */}
        <AnimatePresence initial={false}>
          {showVerify && latestVerificacion && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }} style={{ overflow: 'hidden' }}>
              <Box px={{ base: 6, md: 8 }} py={5} borderTop={`1px solid ${mineral.line}`}>
                <VerificacionPanel verificacion={latestVerificacion} />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </MotionBox>

      {/* Pestañas, con su recuento */}
      <Flex gap={1} borderBottom={`1px solid ${mineral.line}`} flexWrap="wrap">
        {TABS.map((tb) => {
          const active = tab === tb.key
          const n = tabCount[tb.key]
          return (
            <Flex key={tb.key} as="button" {...{ type: 'button' }} position="relative" align="center" gap={2} px={4} py={3} whiteSpace="nowrap"
              fontSize="14px" fontWeight={active ? 700 : 500} color={active ? mineral.ink : mineral.ink3}
              cursor="pointer" _hover={{ color: mineral.ink }} transition="color .15s" onClick={() => setTab(tb.key)}>
              {t(tb.labelKey)}
              {n !== undefined && (
                <Box px={1.5} minW="20px" h="18px" borderRadius="6px" fontFamily={fonts.mono} fontSize="10.5px" fontWeight={600} lineHeight="18px"
                  textAlign="center" bg={active ? acc.color : mineral.sunk} color={active ? '#fff' : mineral.ink3} transition="all .2s">
                  {n}
                </Box>
              )}
              {active && (
                <MotionBox layoutId="expediente-electronico-tab" position="absolute" left={2} right={2} bottom="-1px" h="3px" borderTopRadius="3px" bg={acc.color} />
              )}
            </Flex>
          )
        })}
      </Flex>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.18 }}>
          {tab === 'indice' && (
            <IndiceTable entradas={indice.entradas} onSelect={setSelected} />
          )}
          {tab === 'metadatos' && (
            <MetadatosTab metadatos={meta} />
          )}
          {tab === 'versiones' && (
            <VersionesTab
              code={code}
              versiones={versiones}
              verificaciones={verificaciones}
              onVerified={(v) => setVerificaciones((prev) => ({ ...prev, [v.versionNumber]: v }))}
            />
          )}
          {tab === 'relacionados' && <RelacionadosTab relacionados={indice.relacionados} origen={indice.expedienteOrigen ?? null} />}
        </motion.div>
      </AnimatePresence>

      <EntradaDrawer
        entrada={selected}
        expedienteCode={code}
        onClose={() => setSelected(null)}
      />
    </VStack>
  )
}
